/** Milliseconds since Unix epoch */
export type EpochMs = number;
export type SfoRichText = string & { __brand: 'html' };

/** Strongly-typed identifiers */
export type DocumentId = string; // TODO add brand
export type PartId = string & { __Brand: 'PartId' }; // TODO add brand
export type AuthorId = string; // TODO add brand
export type ResourceId = string & { __Brand: 'ResourceId' };

/** Known resource types (extensible) */
export type ResourceType = 'image' | 'font'| (string & {});

/**
 * ProseMirror node names
 */
export enum SFNodeType {
    /** the root node */
    document = 'doc',
    /** A wrapper for a chapter or similar */
    section = 'section',
    /** A (book) part that may contain individual sub-chapters */
    part = 'part',
    /** A header containing a heading and a subtitle */
    header = 'header',
    paragraph = 'paragraph',
    reference = 'reference',
    text = 'text',
    math = 'math',
    bookmark = 'bookmark',
    footnote = 'footnote',
    citation = 'citation',
    quote = 'quote',
    poetry = 'poetry',
    verbatim = 'verbatim',
    note = 'note',
    /** A forced page break */
    pageBreak = 'pageBreak',
    /** An element that will be replaced with something else in production */
    placeholder = 'placeHolder',
    hardBreak = 'hard_break',
    blockquote = 'blockquote',
    label = 'label',
    caption = 'caption',
    code = 'code_block',
    ordered_list = 'ordered_list',
    bullet_list = 'bullet_list',
    /** Links used for cross referencing (@see MarkType.anchor for hyperlinks) */
    link = 'link',
    hyperlink = 'hyperlink',
    list_item = 'list_item',
    heading = 'heading',
    figure = 'figure',
    image = 'image',
    table = 'table',
    table_row = 'table_row',
    table_cell = 'table_cell',
    table_header = 'table_header',
    subtitle = 'subtitle',
    horizontalRule = 'horizontal_rule'
};

export enum SFMarkType {
    emphasis = 'em',
    strong = 'strong',
    superscript = 'sup',
    subscript = 'sub',
    /** an anchor (hyperlink) */
    anchor = 'anchor',
    bdi = 'bdi',
    indexEntry = 'indexEntry',
    tags = 'tags',
    /**
     * @deprecated Legacy mark from older schema versions (prosemirror-schema-basic).
     * No longer part of the active editor schema, but documents in the database
     * may still carry this mark from before it was removed. Kept here so the
     * export pipeline can render these documents instead of throwing
     * "Unknown mark type code".
     */
    code = 'code'
};

export type rootDoc = SFNodeType.document | SFNodeType.part;
export const isRootDoc = (t: SFNodeType) => ([SFNodeType.document, SFNodeType.part].includes(t));

interface RootDocAttrs {
    id: string;
    direction?: string;
    role?: string;
    schema?: string;
    /** Base level (e.g. for headings) */
    level?: number;
    locale?: string;
    type?: string;
    placement?: string;
    pageBreak?: string;
    /** Chapter numbering */
    numbering?: string;
    /** Page numbering in the section */
    placementNumbering?: string;
    /** An optional part id to reference a node location */
    partId?: string;
    [key: string]: any;
}

/**
 * Metadata for a CSL reference
 */
export interface CSLReferenceMeta {
  source?: 'ai' | 'plain-text' | string;
  reviewed?: boolean;
  originalText?: string | SfoRichText;
  [key: string]: unknown;
}

/**
 * A CSL reference (Citation Style Language) data model used for export rendering
 * @see https://citeproc-js.readthedocs.io/en/latest/csl-json/schema.html
 */
export interface CSLReference {
  id: string;
  [key: string]: any;
  '_meta'?: CSLReferenceMeta;
}

/** The simplified manuscript format a document is saved in (including meta data)
 * It only contains a single linear document and is not split into parts
 * like SciFlow documents usually are. This makes it more suited to direct
 * Word import.
*/
export interface SimplifiedManuscriptFile {
    document: DocumentNode<SFNodeType.document>;
    /** BCP-47 locale, e.g., 'en-US' */
    locale?: string;
    authors: Author[];
    /** The last time the document was saved */
    lastModified: EpochMs;
    /** references as CSL */
    references?: CSLReference[];
    /** A list of all files used in the document (e.g. to replace thumbnails with large resolution images in export) */
    files?: DocumentSnapshotResource[];
    metaData?: unknown;
    /** AI-generated insights / tagging results */
    intelligence?: unknown;
    template?: string;
}

export interface Author {
    userId?: string;
    email: string;
    type?: 'group' | 'author' | string;
    roles?: ('owner' | 'author' | 'editor' | 'comment-only' | 'read-only')[];
    rank: number;
    title?: string;
    name?: string;
    orcid?: string;
    bio?: string;
    authorId: AuthorId;
    comment?: string;
    firstName?: string;
    lastName?: string;
    positions?: Position[];
    funders?: Funder[];
    correspondingAuthor?: boolean;
    equalContribution?: boolean;
    deceased?: boolean;
    hideInPublication?: boolean;
}

/* export interface File {
    isDirectory?: boolean;
    name?: string;
    mimeType?: string;
    children?: File[];
    content?: {
        blob?: string;
        path?: string;
        url?: string;
    }
} */

export interface Funder {
    id: string;
    /*** ISO 3166-1 2-letter (US,CA,GB,..) */
    countryCode: string;
    institution: {
        id: string;
        name: string;
        type?: 'doi' | 'Ringgold' | 'ISNI' | 'ROR' | 'GRID';
    };
    awardIds: string[];
}

/**
 * Represents an individual's position or affiliation within an institution.
 */
export interface Position {
    /**
     * The job title or role of the individual (e.g., Professor, Researcher).
     */
    title: string;
  
    /**
     * The department within the institution (e.g., Department of Biology).
     */
    department: string;
  
    /**
     * An optional division or sub-department within the organization.
     */
    division?: string;
  
    /**
     * The name of the institution (e.g., University of Cambridge).
     */
    institution: string;
  
    /**
     * Array of institution identifiers with their respective types (e.g., Ringgold, GRID, ROR).
     */
    institutionIds: InstitutionId[];
  
    /**
     * The street address of the institution.
     */
    street: string;
  
    /**
     * The city where the institution is located.
     */
    city: string;
  
    /**
     * The state or region where the institution is located (optional).
     */
    region?: string;
  
    /**
     * The postal or ZIP code for the institution's location (optional).
     */
    postalCode?: string;
  
    /**
     * The country where the institution is located.
     */
    country: string;
  
    /**
     * The ISO 3166-1 alpha-2 or alpha-3 country code for the institution's country (optional).
     */
    countryCode?: string;
  
    /**
     * Indicates whether this is the individual's primary position.
     */
    primary: boolean;
  
    /**
     * A unique, URL-friendly identifier for the position (e.g., for linking or referencing).
     */
    slug: string;
  
    /**
     * An optional unique identifier for the affiliation (e.g., used in JATS XML).
     */
    affiliationId?: string;
  
    /**
     * Optional raw string for storing unstructured affiliation information.
     */
    raw?: string;
  
    /**
     * An optional role or position type within the institution.
     */
    role?: string;
  }
  
  /**
   * Represents an identifier for an institution, including its type.
   */
  export interface InstitutionId {
    /**
     * The identifier value (e.g., Ringgold ID, GRID ID, ROR ID).
     */
    id: string;
  
    /**
     * The type of the identifier (e.g., "Ringgold", "GRID", "ROR", "ISNI").
     */
    type: string;
  }

export interface DocumentNode<
  TType extends SFNodeType = SFNodeType,
  TAttrs = any
> {
    type: TType;
    attrs?: TAttrs;
    content?: DocumentNode<any>[];
    text?: string;
    meta?: any;
    marks?: { type: SFMarkType | (string & {}); attrs?: any; }[];
}

/** Back-compat/convenience: DocumentNode with `any` attrs */
export type AnyAttrsNode<T extends SFNodeType = SFNodeType> = DocumentNode<T, any>;

export interface RootDocumentNode extends DocumentNode<SFNodeType.part | SFNodeType.document> {
    attrs: RootDocAttrs;
}

export interface HeadingDocumentNode extends DocumentNode<SFNodeType.heading> {
    type: SFNodeType.heading;
    attrs: RootDocAttrs;
    text: undefined;
    part?: any;
}


/** Discriminants used by exports (e.g., JATS) to infer semantics */
export type PartType = 'abstract' | 'chapter' | 'bibliography' | 'title' | 'appendix' | 'free';
export type PartSchema = 'title' | 'chapter' | 'free';
export type PartPlacement = 'body' | 'front' | 'back' | 'cover';
/** Known roles that trigger special handling in exports. Extend with string to allow custom roles. */
export type PartRole = 'keywords' | 'acknowledgements' | (string & {});

/***
 * A snapshot of the document (without file content).
 * Known values for `type` and `role` are used by exports (e.g., JATS) to infer abstracts, keywords, refs, etc.
 */
export interface DocumentSnapshot {
    /** A unique identifier (may be a technical key) */
    id?: string;
    /** BCP-47 locale, e.g., 'en-US' */
    locale?: string;
    /** A unique string that identifies the document (ideally UUID v4) */
    documentId: DocumentId;
    /** Date as milliseconds since the epoch. */
    createdAt: EpochMs;
    /** The last time the document was modified */
    lastModified?: EpochMs;
    /** Author list */
    authors: Author[];
    /** A list of all document parts in order */
    index: { index: string[]; };
    /** The document title part */
    title?: DocumentSnapshotPart;
    parts: DocumentSnapshotPart[];
    references: CSLReference[];
    /** The publication meta data set in the document (e.g. a DOI, custom margins etc.) */
    metaData?: any;
    files: DocumentSnapshotResource[];
    /** A sequential document version number */
    documentVersion?: number;
    /** Any changes logged on the document */
    logs?: any[];
    /** The absolute number of the part in a collection */
    collectionIndex?: number;
}

/**
 * A collection of documents.
 */
export interface ProjectSnapshot extends DocumentSnapshot {
    projectId?: string;
    projectTitle?: string;
    projectLabel?: string;
}

/** A part of the document that may be edited independently.
 * Only partId and document are mandatory.
 * Known values for `type` and `role` are used by exports (e.g., JATS) to infer abstracts, keywords, refs, etc.
*/
export interface DocumentSnapshotPart {
    /** A unique identifier (may be a technical key) */
    id?: PartId;
    /** BCP-47 locale, e.g., 'en-US' */
    locale?: string;
    /** A unique identifier that is referenced in the document index (also partId) */
    partId: PartId;
    /** A custom citation style to be used (changing will trigger a disruption of bibliographic rendering) */
    citationStyle?: string;
    /** The part title (h1) */
    title?: string;
    /** ProseMirror document node */
    document: DocumentNode<rootDoc>;
    /** The type of the part. Defaults to chapter */
    type?: PartType;
    /** The schema being applied to the ProseMirror document (defaults to chapter) */
    schema?: PartSchema;
    /** The placement of the part in the document */
    placement?: PartPlacement;
    /** The numbering counter style */
    numbering?: counterStyle;
    /** The semantic role of the part (e.g. methods) */
    /** Known values like 'keywords' and 'acknowledgements' are used by the JATS export */
    role?: PartRole;
    options?: any;
    /** A label to apply to the part (like coverpage) */
    label?: string;
    /** Reference to the parent document (avoids circular structures) */
    parentDocumentId?: string;
    /** The absolute number of the part in a collection */
    collectionIndex?: number;
}

/** Title part: drives <article-title>/<subtitle> extraction */
export interface TitlePart extends DocumentSnapshotPart {
  type: 'title';
  schema?: Extract<PartSchema, 'title'>;
  placement?: Extract<PartPlacement, 'front' | 'cover'>;
}

/** Abstract part: emitted as <abstract> in JATS
 * Multiple abstract parts are allowed; exporters typically select by `locale` and may concatenate if needed.
 */
export interface AbstractPart extends DocumentSnapshotPart {
  type: 'abstract';
  placement?: Extract<PartPlacement, 'front' | 'body'>;
}

/** Bibliography part: rendered as <ref-list> (content source)
 * Multiple bibliography parts are allowed (e.g., per locale) and may appear in body or back.
 */
export interface BibliographyPart extends DocumentSnapshotPart {
  type: 'bibliography';
  /** Bibliography can live in the body (end of article) or in the back matter */
  placement?: Extract<PartPlacement, 'body' | 'back'>;
}

/** Keywords part: the first paragraph is parsed into <kwd-group>.
 * Multiple keyword parts are allowed; exporters typically select by `locale`.
 */
export interface KeywordsPart extends DocumentSnapshotPart {
  role: 'keywords';
  /** Usually appears in front matter or body */
  placement?: Extract<PartPlacement, 'front' | 'body'>;
}

/** Acknowledgements part: rendered as <ack> in back matter */
export interface AcknowledgementsPart extends DocumentSnapshotPart {
  role: 'acknowledgements';
  placement?: Extract<PartPlacement, 'back'>;
}

/** Convenience union of parts that have special export handling */
export type KnownExportPart =
  | TitlePart
  | AbstractPart
  | BibliographyPart
  | KeywordsPart
  | AcknowledgementsPart;

export const isTitlePart = (p: DocumentSnapshotPart): p is TitlePart => p.type === 'title';
export const isAbstractPart = (p: DocumentSnapshotPart): p is AbstractPart => p.type === 'abstract';
export const isBibliographyPart = (p: DocumentSnapshotPart): p is BibliographyPart => p.type === 'bibliography';
export const isKeywordsPart = (p: DocumentSnapshotPart): p is KeywordsPart => p.role === 'keywords';
export const isAcknowledgementsPart = (p: DocumentSnapshotPart): p is AcknowledgementsPart => p.role === 'acknowledgements';

/** https://www.w3.org/TR/css-counter-styles-3/#predefined-counters */
export type counterStyle = undefined | 'none' | 'decimal' | 'lower-roman' | 'upper-roman' | 'lower-alpha' | 'double-lower-alpha' | 'upper-alpha' | string;

export interface PlacementOption {
    numbering?: counterStyle;
}

/** A resource that is referenced in the document. */
export interface DocumentSnapshotResource {
    /** Unique identifier (should be equal to figure ids if used in the document) */
    id: ResourceId;
    /** The resource type */
    type: ResourceType;
    /** URL that can be used to fetch the resource */
    url: string;
    name?: string;
    /** Optional mime type */
    mimeType?: string;
    /** Whether the file content is inlined in the content property */
    inline?: boolean;
    /** The inlined file (@see inline) */
    content?: any;
    dimensions?: {
        width: string | number;
        height: string | number;
    };
    /** Additional attributes */
    [key: string]: unknown;
    /** Locales the resource is available for */
    locales?: string[];
}

export interface DocumentSnapshotImage extends DocumentSnapshotResource {
    type: 'image';
    dimensions: {
        height: number | string;
        width: number | string;
    };
}

/**
 * Result returned by the export handler.
 *
 * Describes what the controller produced (file, string, JSON, path or a
 * redirect descriptor), along with useful metadata for the caller/UI.
 *
 * - status: HTTP status code for the response.
 * - type: shape of `content` (json | blob | string | path | redirect).
 *   - json: `content` is JSON‑serializable data.
 *   - blob: `content` is a Buffer/Uint8Array (binary payload).
 *   - string: `content` is a UTF‑8 string (e.g., HTML/XML).
 *   - path: `content` is an absolute filesystem path to a generated file.
 *   - redirect: `content` carries a stored file descriptor used by the client to redirect/download.
 * - content: payload as described above.
 * - fileName: suggested filename for downloads (e.g., MyDoc.pdf).
 * - documentVersion: document version used for the export.
 * - mimeType: Content‑Type for the response when applicable.
 * - headers: extra response headers to return (e.g., Content‑Disposition).
 * - citationString: human‑readable citation/slug used as base filename.
 * - citation: CSL JSON used to build citations/filenames.
 * - previews: small preview images for the first pages (PDF only).
 * - stats: stats for artifacts (e.g., sha256, bytes, pages for PDF).
 * - logs: structured messages captured during the export (for UI).
 * - patches: JSON‑Patch operations applied to document metadata.
 * - template: resolved template/configuration snapshot used.
 * - cleanup: optional callback that must be called to delete temporary files.
 */
export interface HandleRequestResult {
  status: number;
  type: "json" | "blob" | "string" | "path" | "redirect";
  content: any;
  fileName?: string;
  documentVersion: number;
  mimeType?: string;
  headers: any;
  citationString?: string;
  citation?: any;
  previews?: { page: string; url: string }[];
  stats?: { bytes: number; pages: number; sha256: number };
  logs?: any[];
  document: DocumentSnapshot;
  patches?: any[];
  template?: any;
  cleanup?: () => void;
  /** URL to sourcemap JSON file (when sourceMap=true was requested) */
  sourcemapUrl?: string;
  /** Hash of metadata for cache invalidation */
  metaDataHash?: string;
  /** Hash of authors for cache invalidation */
  authorsHash?: string;
  /** Hash of other fields (locale, references, options, documentAccess, modifiedAt) for cache invalidation */
  otherHash?: string;
}

/**
 * Section: Types
 * The data needed to start a export job (async and sync).
 */
export interface HandleRequestParams {
  params?: {
    templateSlug?: string;
    documentId?: string;
    projectId?: string;
    extension?: string;
    runner?:
      | "princexml"
      | "html"
      | "xml"
      | "jatsxml"
      | "bitsxml"
      | "raw"
      | "epub"
      | "pandoc"
      | string;
  };
  query?: {
    transactionId?: string;
    locale?: string;
    background?: string | boolean;
    separateFonts?: string | boolean;
    inline?: string | boolean;
    storeFile?: string | boolean;
    forceRecreate?: string | boolean;
    variant?: string | string[];
    debug?: string | boolean;
    version?: string;
    citationStyle?: string;
    transformationId?: string;
    /** Generate a sourcemap JSON file alongside the export (for insights/analysis) */
    sourceMap?: string | boolean;
  };
  headers?: {
    accept?: string;
    authorization: string;
  };
  viewer?: { id: string };
  /** Optional summary of the request to identify repeat requests */
  exportConfig?: {
    value: any;
    /** md5 hash of the stringified value (hex digest) */
    hash: string;
    scope?: {
      ids: string[];
    };
  };
  /**
   * Build variables passed to configure an export or trigger additional plugins. Flattened key→value map produced from VariableInput[]
   * Examples: { 'theme': 'dark', 'figures.compress': true }
   */
  variables?: Record<string, any>;
  /**
   * Optional metadata per variable key (visibility, scope, description). Useful for logging and policy.
   */
  variablesMeta?: Record<
    string,
    { isSecret?: boolean; scope?: string; description?: string }
  >;
  /**
   * A pre-built document snapshot that bypasses the document service fetch.
   * Used by the template preview endpoint to synthesize a document from
   * the template's Kind: Content configuration.
   */
  syntheticDocument?: DocumentSnapshot;
}
